import React from 'react';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import { FaArrowRight } from 'react-icons/fa6';

import {
  GoToPageButton,
  GoToPageInput,
  InputContainer,
  ItemsPerPageSelect,
  PaginationButton,
  PaginationContainer,
  PaginationContent,
  PaginationForm,
  PaginationItem,
  PaginationLabel,
  PaginationList,
  PaginationNav,
  SelectAngleDownIcon,
  SelectContainer,
  ShowingText,
  TotalText,
} from './styles';

export interface PaginationProps {
  rowsPerPage: number;
  totalItems: number;
  paginate: (pageNumber: number) => void;
  currentPage: number;
  showingStart: number;
  showingEnd: number;
  handleItemsPerPageChange: (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => void;
  itemsPerPageOptions: number[];
  goToPageValue: string;
  onGoToPageChange: (value: string) => void;
  onGoToPageSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  rowsPerPage,
  totalItems,
  paginate,
  currentPage,
  showingStart,
  showingEnd,
  handleItemsPerPageChange,
  itemsPerPageOptions,
  goToPageValue,
  onGoToPageChange,
  onGoToPageSubmit,
}) => {
  const pageNumbers = [];
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const maxPagesToShow = 7;
  const halfPagesToShow = Math.floor(maxPagesToShow / 2);
  let startPage = currentPage - halfPagesToShow;
  let endPage = currentPage + halfPagesToShow;

  if (startPage <= 0) {
    startPage = 1;
    endPage = Math.min(maxPagesToShow, totalPages);
  }

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }

  return (
    <PaginationContainer>
      <ShowingText>
        <TotalText>Mostrando:</TotalText> {showingStart} a {showingEnd} /{' '}
        <TotalText>Total de</TotalText> {totalItems}
      </ShowingText>
      <PaginationNav>
        <PaginationContent>
          <PaginationList>
            <PaginationItem>
              <PaginationButton
                type="button"
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <FaAngleLeft />
              </PaginationButton>
            </PaginationItem>
            {pageNumbers.slice(startPage - 1, endPage).map((number) => (
              <PaginationItem key={`page-${number}`}>
                <PaginationButton
                  type="button"
                  onClick={() => paginate(number)}
                  $active={currentPage === number}
                >
                  {number}
                </PaginationButton>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationButton
                type="button"
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <FaAngleRight />
              </PaginationButton>
            </PaginationItem>
          </PaginationList>

          <PaginationList>
            <PaginationItem>
              <SelectContainer>
                <ItemsPerPageSelect
                  value={rowsPerPage}
                  onChange={handleItemsPerPageChange}
                >
                  {itemsPerPageOptions.map((option) => (
                    <option key={`option-${option}`} value={option}>
                      Mostrar {option}
                    </option>
                  ))}
                </ItemsPerPageSelect>
                <SelectAngleDownIcon />
              </SelectContainer>
            </PaginationItem>
            <PaginationItem>
              <PaginationForm onSubmit={onGoToPageSubmit}>
                <PaginationLabel>
                  <InputContainer>
                    <GoToPageInput
                      type="text"
                      placeholder="Página"
                      value={goToPageValue}
                      onChange={(event) => onGoToPageChange(event.target.value)}
                    />
                    <GoToPageButton type="submit">
                      <FaArrowRight />
                    </GoToPageButton>
                  </InputContainer>
                </PaginationLabel>
              </PaginationForm>
            </PaginationItem>
          </PaginationList>
        </PaginationContent>
      </PaginationNav>
    </PaginationContainer>
  );
};

export default Pagination;
